import React, { useState } from "react";
import { Box, Typography, Paper, Modal, IconButton, Fade, Backdrop, Slider } from "@mui/material";
import {
  PlayCircleOutline as PlayIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  InsertDriveFile as DocumentIcon,
  Headphones as AudioIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
} from "@mui/icons-material";
import MessageContent from "./MessageContent";
import MessageStatusIcon from "./MessageStatusIcon";
import { formatAudioTime } from "../../utils/uploads";
import {
  WHATSAPP_MESSAGE_USER_BG,
  WHATSAPP_MESSAGE_BOT_BG,
  WHATSAPP_TEXT_COLOR,
  WHATSAPP_SECONDARY_TEXT_COLOR
} from '../../constants/whatsapp';
import { QuoteBubble } from "./QuoteBubble";

interface MediaMessageProps {
  message: {
    id: string;
    content: string;
    direction: "inbound" | "outbound";
    created_at: string;
    message_type: string;
    is_sent?: boolean;
    is_delivered?: boolean;
    is_read?: boolean;
    is_failed?: boolean;
    msg_media_metadata?: {
      media_id?: string;
      media_url?: string;
      media_type?: string;
      media_size?: number;
      media_filename?: string; // used by some API versions
      file_name?: string;      // used by WhatsApp delivery API
    };
    platform_message_id?: string;
    msg_context_metadata?: {
      reaction_emoji?: string | null;
      reaction_message_platform_id?: string;
      reaction_timestamp?: string;
      context_platform_message_id?: string;
      context_message_id?: string;
    };
  };
  formatTime: (dateString: string) => string;
  allMessages?: any[];
  onScrollToQuote?: (id: string) => void;
  smbName?: string;
  leadName?: string;
}

const MediaMessage: React.FC<MediaMessageProps> = ({
  message,
  formatTime,
  allMessages,
  onScrollToQuote,
  smbName,
  leadName,
}) => {
  const [open, setOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const handleTogglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch((error) => {
              console.error("Audio playback failed:", error);
              setIsPlaying(false);
            });
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioError = (e: any) => {
    console.error("Audio element error", e);
    setIsPlaying(false);
  };

  const handleSliderChange = (_: any, newValue: number | number[]) => {
    if (audioRef.current && typeof newValue === 'number') {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };


  const isOutbound = message.direction === "outbound";
  const mediaUrl = message.msg_media_metadata?.media_url;
  const caption = message.content;
  const mediaType = message.msg_media_metadata?.media_type || message.message_type;
  const getFallbackFileName = () => {
    switch (mediaType) {
      case 'image': return 'image';
      case 'video': return 'video';
      case 'audio': return 'audio';
      case 'document': return 'document';
      default: return 'file';
    }
  };

  const fileName = message.msg_media_metadata?.media_filename
    || message.msg_media_metadata?.file_name
    || getFallbackFileName();
  const contextMessageId = message.msg_context_metadata?.context_message_id;

  const MAX_WIDTH = 300;
  const MAX_HEIGHT = 400;



  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent modal from closing if clicking download inside it
    if (!mediaUrl) return;

    try {
      const response = await fetch(mediaUrl, {
        cache: 'no-cache',
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  if (!mediaUrl) {
    // Type-aware fallback when URL is unavailable
    if (mediaType === 'audio') {
      return (
        <Paper sx={{ p: 1.5, maxWidth: MAX_WIDTH, bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AudioIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            <Typography variant="body2" color="text.secondary">Audio unavailable</Typography>
          </Box>
        </Paper>
      );
    }
    if (mediaType === 'document') {
      return (
        <Paper sx={{ p: 1.5, maxWidth: MAX_WIDTH, bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight="medium" noWrap>{fileName !== 'file' ? fileName : 'Document'}</Typography>
              <Typography variant="caption" color="text.secondary">Document unavailable</Typography>
            </Box>
          </Box>
        </Paper>
      );
    }
    if (mediaType === 'image') {
      return (
        <Paper sx={{ p: 1.5, maxWidth: MAX_WIDTH, bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            <Typography variant="body2" color="text.secondary">Image unavailable</Typography>
          </Box>
        </Paper>
      );
    }
    if (mediaType === 'video') {
      return (
        <Paper sx={{ p: 1.5, maxWidth: MAX_WIDTH, bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            <Typography variant="body2" color="text.secondary">Video unavailable</Typography>
          </Box>
        </Paper>
      );
    }
    return (
      <Paper sx={{ p: 2, maxWidth: MAX_WIDTH, bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : 'grey.100', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">Media not available</Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          p: 0.5,
          maxWidth: MAX_WIDTH,
          width: "fit-content",
          ml: isOutbound ? "auto" : 0,
          bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG,
          color: isOutbound ? WHATSAPP_TEXT_COLOR : WHATSAPP_TEXT_COLOR,
          borderRadius: 2,
          boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
          position: "relative",
        }}
      >
        {contextMessageId && (
          <QuoteBubble
            contextMessageId={contextMessageId}
            allMessages={allMessages}
            onScrollToQuote={onScrollToQuote}
            isOutbound={isOutbound}
            leadName={leadName}
            smbName={smbName}
          />
        )}

        <Box
          sx={{
            borderRadius: 1.5,
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "rgba(0,0,0,0.02)",
            maxHeight: MAX_HEIGHT,
            width: "100%",
          }}
        >
          {mediaType === "image" ? (
            <Box
              component="img"
              src={mediaUrl}
              alt="Shared image"
              onClick={() => setOpen(true)} // Open Lightbox
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: MAX_HEIGHT,
                objectFit: "contain",
                display: "block",
                cursor: "pointer", // Indicate it's clickable
                transition: "opacity 0.2s",
                "&:hover": { opacity: 0.9 }
              }}
              loading="lazy"
            />
          ) : mediaType === "video" ? (
            <Box
              component="video"
              src={mediaUrl}
              controls
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: MAX_HEIGHT,
                display: "block",
                bgcolor: "black",
              }}
            />
          ) : mediaType === "audio" ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                width: "100%",
                minWidth: 260,
                bgcolor: isOutbound ? "rgba(255,255,255,0.1)" : "rgba(253, 250, 250, 1)",
                borderRadius: 1.5,
              }}
            >
              <audio
                ref={audioRef}
                src={mediaUrl}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
                onError={handleAudioError}
                preload="metadata"
                style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
              />
              <IconButton
                size="small"
                onClick={handleTogglePlay}
                disabled={!mediaUrl}
                sx={{
                  border: `2px solid ${isOutbound ? "#999696ff" : "#919191"}`,
                  color: isOutbound ? "#999696ff" : "#919191",
                  bgcolor: "transparent",
                  width: 32,
                  height: 32,
                  "&:hover": { bgcolor: isOutbound ? "rgba(213, 206, 206, 0.1)" : "rgba(0,0,0,0.05)" }
                }}
              >
                {isPlaying ? <PauseIcon sx={{ fontSize: 18 }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
              </IconButton>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Slider
                  size="small"
                  value={currentTime}
                  max={duration || 100}
                  onChange={handleSliderChange}
                  sx={{
                    color: "#919191",
                    height: 3,
                    padding: "6px 0",
                    "& .MuiSlider-thumb": {
                      display: "none",
                    },
                    "& .MuiSlider-rail": {
                      opacity: 0.5,
                      bgcolor: "#ddd",
                    },
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: -0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#919191" }}>
                    {formatAudioTime(currentTime)}
                  </Typography>
                  {duration > 0 ? (
                    <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#919191" }}>
                      {formatAudioTime(duration)}
                    </Typography>
                  ) : fileName.toLowerCase().endsWith(".amr") ? (
                    <Typography variant="caption" sx={{ fontSize: "0.55rem", color: "error.main" }}>
                      Format not supported
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {fileName.toLowerCase().endsWith(".amr") && (
                  <IconButton size="small" onClick={handleDownload} sx={{ color: "#919191" }}>
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: "#e97979", // Pinkish red like WhatsApp
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  <AudioIcon sx={{ color: "#fff", fontSize: 20 }} />
                </Box>
              </Box>
            </Box>
          ) : mediaType === "document" ? (
            <Box
              component="a"
              href={mediaUrl}
              download={fileName}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                width: "100%",
                textDecoration: "none",
                color: "inherit",
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <DocumentIcon sx={{ fontSize: 32, color: 'text.secondary', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {fileName}
                </Typography>
                {message.msg_media_metadata?.media_size && (
                  <Typography variant="caption" color="text.secondary">
                    {(message.msg_media_metadata.media_size / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ py: 4, px: 8, textAlign: "center" }}>
              <PlayIcon sx={{ fontSize: 40, color: "grey.500" }} />
            </Box>
          )}
        </Box>

        <Box sx={{ px: 0.5, pt: 0.5, position: "relative" }}>
          {caption && <Typography component='div' variant='body2'>
            <MessageContent text={caption} />
          </Typography>}
          <Typography
            variant='caption'
            sx={{
              color:
                isOutbound
                  ? WHATSAPP_SECONDARY_TEXT_COLOR
                  : 'text.secondary',
              fontSize: '0.7rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
              mt: 0.5,
            }}
          >
            {formatTime(message.created_at)}
            <MessageStatusIcon message={message} />
          </Typography>
        </Box>
      </Paper>

      {/* --- LIGHTBOX MODAL --- */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: { timeout: 500, sx: { bgcolor: "rgba(0, 0, 0, 0.9)" } },
        }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95vw", md: "70vw" }, // Large preview
              maxWidth: "1000px",
              maxHeight: "90vh",
              outline: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Controls Row */}
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", mb: 1, gap: 1 }}>
              <IconButton
                onClick={handleDownload}
                sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
              >
                <DownloadIcon />
              </IconButton>
              <IconButton
                onClick={() => setOpen(false)}
                sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Large Image Preview */}
            <Box
              component="img"
              src={mediaUrl}
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: 1,
                boxShadow: 24,
              }}
            />

            {caption && (
              <MessageContent text={caption} />
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default MediaMessage;